import { baseApi } from '../api';
import { Salesnews } from '../../models/salesnews';
import { AircallUserResponse } from '../../models/LoginRequests';
import { OperatorsResponse } from '../../models/Operators';
import { Facility } from '../../models/facility';
import { OperatorsalertModel } from '../../models/AlertsByoperator';
import { FacilityLocationCodeResponse } from '../../models/FacilityLocationCode';
import { FacilitySearchResponse } from '../../models/FacilitySearchList';
import { Deal } from '../../models/Deal';
import { AvailableUnitsResponse } from '../../models/AvailableUnits';
import { SetupResponse } from '../../models/SetupItem';
import { AllOperatorsalertModel } from '../../models/AllOperatoralerts';
import { FacilitiesDistancedata } from '../../models/Distancefacilitydata';
import { SettingsData } from '../../models/SettingsData';
import { OccupancyData } from '../../models/OccupancyData';
import { ReservationDetails } from '../../models/ReservationDetails';
import { NotesResponse } from '../../models/Note';
import { UpdateReservationDetails } from '../../models/UpdateReservationDetails';
import { MoveInCostRequest, MoveInCostResponse } from '../../models/MoveInCost';
import { ProtectionPlans } from '../../models/prodectiondata';
import { OperatorAlert } from '../../models/OperatorGuides';
import { StatesResponse } from '../../models/State';
import { TagsResponse } from '../../models/Tag';
import {merchandise} from '../../models/Merchandisedata';

/** Minimal shape for GET calls/search results */
export type CallSearchRow = {
    id: number;
    status: string;
    answered_at: number | null;
    direction?: string;
};

export type CallsSearchResponse = {
    calls?: CallSearchRow[];
};

/** GET calls/search — `from` required; `phone_number` and `user_id` optional */
export type SearchCallsParams = {
    from: number;
    phone_number?: string;
    user_id?: number | string;
};

// Extend the base API with multiple endpoints

const apiBaseUrl = window.config.apiUrl.replace(/\/+$/, '');

export const apiConfig = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        // Endpoint 1: Get Sales News
        getSalesNews: builder.query<Salesnews, void>({
            query: () => ({
                url: 'sales-news',
            }),
        }),

        // Endpoint 2: Get Aircall User by email (query param encoding; do not call with empty email)
        getAircallUser: builder.query<AircallUserResponse, string>({
            query: (email) => ({
                url: 'air-call/users',
                params: { email },
            }),
        }),

        // Endpoint 3: Get Operators
        getOperators: builder.query<OperatorsResponse, void>({
            query: () => ({
                url: 'operators',
            }),
        }),

        // Endpoint 4: Get Facilities by Operator ID
        getFacilities: builder.query<FacilityLocationCodeResponse, number>({
            query: (operatorId) => ({
                url: `operators/${operatorId}/facilities`, // Correct template literal syntax
            }),
        }),
        // Endpoint 5: Get Facilities in Dashboard
        getFacilityById: builder.query<Facility, number>({
            query: (facilityId) => ({
                url: `facilities/${facilityId}?with[]=protectionPlans&with[]=guides&with[]=alerts&with[]=promotions`,
            }),
            transformResponse: (response: { facility: Facility }) => {
                return response.facility;
            }
        }),
        // Endpoint 6: Get the List of Facilities
        getFacilitiesSearch: builder.query<FacilitySearchResponse, void>({
            query: () => '/facilities',
            // Return the entire facility search response, not just data
            transformResponse: (response: any) => response.facilities,
        }),

        // Endpoint 7: Get the List of Operator Alert by operatorid
        getFacilityOperatorAlerts: builder.query<OperatorsalertModel, number>({
            query: (operatorid) => `operators/${operatorid}?with[]=alerts`,
        }),

        // Endpoint 8: Get the List of Deals based on facility Id
        fetchDealsByFacility: builder.query<{ deals: Deal[] }, number>({
            query: (facilityId) => `facilities/${facilityId}/deals?realtime=1`,
            providesTags: (result, error, facilityId) => [{ type: 'Deals', id: facilityId }],
        }),

        // Endpoint 9: Get the List of Available Units based on facility Id
        getAvailableUnitsData: builder.query<AvailableUnitsResponse, number>({
            query: (facilityId) => `facilities/${facilityId}/unit-type-price-list`,  // Replace 'facility' with your API endpoint path
        }),
        // Endpoint 10: Get the List of Operator Alert
        getOperatorAlerts: builder.query<AllOperatorsalertModel, void>({
            query: () => ({
                url: 'operators?with[]=alerts'
            }),
        }),

        // Endpoint 11: post the  reservation data
        postReservation: builder.mutation({
            query: ({ reservationData, facilityId }) => ({
                url: `facilities/${facilityId}/deals`,
                method: 'POST',
                body: reservationData,
            }),
        }),

        // Endpoint 12: Get the Setup
        getSetup: builder.query<SetupResponse, void>({
            query: () => 'setup',
        }),

        //Endpoint 13: facility distance search data
        getFacilityDistanceSearch: builder.query<FacilitiesDistancedata, number>({
            query: (operatorid) => ({
                url: `operators/${operatorid}/facilities?select[]=latitude&select[]=longitude&select[]=address&select[]=operator_id&select[]=location_code&select[]=name&select[]=city&select[]=state&select[]=location_code&select[]=phone`,
            }),
        }),

        // Endpoint 14: get setting data based on facility id
        getSettingData: builder.query<SettingsData, number>({
            query: (facilityid) => ({
                url: `facilities/${facilityid}/settings`,
            }),
        }),

        //Endpoint 15: get deals by deals id
        getDealByid: builder.query<Deal, { facilityid: number; dealsid: number }>({
            query: ({ facilityid, dealsid }) => ({
                url: `facilities/${facilityid}/deals/${dealsid}`,
            }),
            transformResponse: (response: { deal: Deal }) => {
                return response.deal;
            }
        }),

        // Endpoint 16: get occupancy data based on facility id  /occupants?email=richard@
        getOccupancyData: builder.query<OccupancyData[], { facilityid: number; email: string; firstname: string; lastname: string; accesscode: string; phone: string }>({
            query: ({ facilityid, email, firstname, lastname, accesscode, phone }) => ({
                url: `facilities/${facilityid}/occupants?email=${email}&first_name=${firstname}&last_name=${lastname}&access_code=${accesscode}&phone=${phone}`,
            }),
        }),

        // Endpoint 17: Fetch reservation details based on facility Id and deal Id
        fetchReservationDetails: builder.query<ReservationDetails, { facilityId: number; dealId: number }>({
            query: ({ facilityId, dealId }) => `facilities/${facilityId}/deals/${dealId}`, // Correct order
        }),

        // Endpoint 18: Fetch Notes based on facility Id and deal Id
        fetchNotes: builder.query<NotesResponse, { facilityId: number; dealId: number }>({
            query: ({ facilityId, dealId }) => {
                console.log(`Fetching notes for facilityId: ${facilityId} and dealId: ${dealId}`);
                return `facilities/${facilityId}/deals/${dealId}/notes?realtime=1`;
            },
        }),

        // Endpoint 19: Define the mutation for adding a note
        addNote: builder.mutation({
            query: ({ facilityId, dealId, noteData }) => ({
                url: `/facilities/${facilityId}/deals/${dealId}/notes`,
                method: 'POST',
                body: noteData,
            }),
        }),

        // Endpoint 20: Patch Update Reservation Details
        patchReservationDetails: builder.mutation<void, { facilityId: number; dealId: number; body: UpdateReservationDetails }>({
            query: ({ facilityId, dealId, body }) => ({
                url: `facilities/${facilityId}/deals/${dealId}`,
                method: 'PATCH',
                body, // Send the updated data
            }),
            async onQueryStarted({ facilityId, dealId, body }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    if (!body?.external_unit_id) return;

                    dispatch(
                        apiConfig.util.updateQueryData('fetchDealsByFacility', facilityId, (draft) => {
                            const deal = draft?.deals?.find(
                                (d) =>
                                    String(d.external_id) === String(dealId) ||
                                    Number(d.id) === Number(dealId)
                            );
                            if (deal) {
                                deal.external_unit_id = String(body.external_unit_id);
                            }
                        })
                    );

                    dispatch(
                        apiConfig.util.updateQueryData(
                            'fetchReservationDetails',
                            { facilityId, dealId },
                            (draft) => {
                                if (draft?.deal) {
                                    draft.deal.external_unit_id = String(body.external_unit_id);
                                }
                            }
                        )
                    );
                } catch {
                    // Keep cache unchanged when PATCH fails.
                }
            },
            invalidatesTags: (result, error, { facilityId }) => [{ type: 'Deals', id: facilityId }],
        }),

        // Endpoint 21: Implementation of Move In Cost Response
        getMoveInCost: builder.mutation<MoveInCostResponse, { data: MoveInCostRequest, facilityId: number }>({
            query: ({ data, facilityId }) => ({
                // Use an absolute URL to avoid any baseUrl resolution mismatch.
                url: `${apiBaseUrl}/facilities/${facilityId}/move-in-cost`,
                method: 'POST',
                body: data,
            }),
        }),

        // Endpoint 22: rent save 
        postRent: builder.mutation({
            query: ({ rented, facilityId }) => ({
                url: `facilities/${facilityId}/move-in`,
                method: 'POST',
                body: JSON.stringify(rented), // Ensure to stringify the rent object
                headers: {
                    'Content-Type': 'application/json', // Set the content type
                    // Add any necessary authorization headers here
                },
            }),
        }),

        // Endpoint 23: Get the Protection Plan
        getProtectionPlan: builder.query<ProtectionPlans, number>({
            query: (facilityId) => `facilities/${facilityId}/protection-plans`,
        }),

        // Endpoint 24: Get the Operator Guide
        getOperatorGuides: builder.query<OperatorAlert, number>({
            query: (operatorId) => `operators/${operatorId}/guides`,
        }),

        // Endpoint 25: Get the List of State
        getStates: builder.query<StatesResponse, void>({
            query: () => 'states',
        }),

        // Endpoint 26: Pause the Call
        pauseCall: builder.mutation({
            query: (id) => ({
                url: `calls/${id}/pause`,
                method: 'POST', // or whatever method you need
            }),
        }),

        // Endpoint 27: Resume the Call
        resumeCall: builder.mutation({
            query: (callId) => ({
                url: `/calls/${callId}/resume`,
                method: 'POST',
            }),
        }),

        // Endpoint 28: rent save 
        postSpeedeRent: builder.mutation({
            query: ({ rented, facilityId }) => ({
                url: `facilities/${facilityId}/speederent`,
                method: 'POST',
                body: JSON.stringify(rented), // Ensure to stringify the rent object
                headers: {
                    'Content-Type': 'application/json', // Set the content type
                    // Add any necessary authorization headers here
                },
            }),
        }),

        // Endpoint 29: Get the Tag
        getTags: builder.query<TagsResponse, void>({
            query: () => 'calls/tags',
        }),

        // Endpoint 30: Add the Tag
        addTagToCall: builder.mutation({
            query: ({ callId, tagData }) => ({
                url: `calls/${callId}/tags`,
                method: 'POST',
                body: tagData,
            }),
        }),

        // Endpoint 31: AddNoteToLedger
        addNoteToLedger: builder.mutation({
            query: ({ facilityId, ledgerId, note, agent }) => ({
                url: `/facilities/${facilityId}/ledgers/${ledgerId}/notes`,
                method: 'POST',
                body: { note, agent },
            }),
        }),

        // Endpoint 32: AddtoSMS
        addSMS: builder.mutation({
            query: ({ facilityId, occupantid, data }) => ({
                url: `/facilities/${facilityId}/occupants/${occupantid}/sms`,
                method: 'POST',
                body: data,
            }),
        }),
        // Endpoint 33: Getmerchandise
        getMerchandise: builder.query<merchandise[], number>({
            query: (facilityId) => `facilities/${facilityId}/merchandise`,
        }),

        // Endpoint 34: Similar Units
        getSimilarUnits: builder.query<any[], { FacilityId: number; ExternalUnitId: number }>({
            query: ({ FacilityId, ExternalUnitId }) => `facilities/${FacilityId}/similar-units/${ExternalUnitId}`,
          }),

        searchCallsByPhone: builder.query<CallsSearchResponse, SearchCallsParams>({
            query: ({ from, phone_number, user_id }) => {
                const params: Record<string, string | number> = { from };
                if (phone_number != null && phone_number !== '') {
                    params.phone_number = phone_number;
                }
                if (user_id != null && user_id !== '') {
                    params.user_id =
                        typeof user_id === 'number' ? user_id : Number(user_id);
                }
                return {
                    url: 'calls/search',
                    params,
                };
            },
        }),
    }),

});


// Export hooks for each endpoint
export const {
    useGetSalesNewsQuery,
    useGetAircallUserQuery,
    useGetOperatorsQuery,
    useGetFacilitiesQuery,
    useGetFacilityByIdQuery,
    useGetFacilitiesSearchQuery,
    useGetFacilityOperatorAlertsQuery,
    useFetchDealsByFacilityQuery,
    useGetAvailableUnitsDataQuery,
    useGetOperatorAlertsQuery,
    usePostReservationMutation,
    useGetSetupQuery,
    useGetFacilityDistanceSearchQuery,
    useGetSettingDataQuery,
    useGetDealByidQuery,
    useLazyGetOccupancyDataQuery,
    useFetchReservationDetailsQuery,
    useFetchNotesQuery,
    useAddNoteMutation,
    usePatchReservationDetailsMutation,
    useGetMoveInCostMutation,
    usePostRentMutation,
    useGetProtectionPlanQuery,
    useGetOperatorGuidesQuery,
    useGetStatesQuery,
    usePauseCallMutation,
    useResumeCallMutation,
    usePostSpeedeRentMutation,
    useGetTagsQuery,
    useAddTagToCallMutation,
    useAddNoteToLedgerMutation,
    useAddSMSMutation,
    useGetMerchandiseQuery,
    useGetSimilarUnitsQuery,
    useLazySearchCallsByPhoneQuery,
} = apiConfig;
